import { AfterViewInit, Component, Input, ViewChild, ViewContainerRef } from "@angular/core";
import { PonderDataClient } from "@apiserver-rest/ponder-data-client";
import { DeckCard, GameType, GetDeckResponse } from "@apiserver-rest/ponder-data-client-base";
import { MagicDeckComponent } from "@ponder-features/magic/deck/magic-deck.component";
import { LoadingComponent } from "@ponder-shared/loading/loading.component";
import { Subscription, take } from "rxjs";
import { DeckBaseComponent } from "./deck-base.component";
import { Title } from "@angular/platform-browser";
import { EventBroker } from "@ponder-core/events/eventBroker";
import { addSegmentToList, initializeUserSegmentArray } from "@ponder-features/header/navbar/navbar.component";
import { AlternateCardModalAction, DeckAlternateCardModalComponent } from "./tools/deck-alternate-card-modal.component";
import { Portable404Component } from "@ponder-features/general/404/404portable.component";

@Component({
    imports: [LoadingComponent, Portable404Component],
    templateUrl: "./deck.component.html"
})
export class DeckComponent implements AfterViewInit {
    @Input() public deckId!: string;
    @Input() public username!: string;

    @ViewChild("deckTemplate", { read: ViewContainerRef }) private deckVcr!: ViewContainerRef;
    
    public isLoading: boolean = true;
    public loadingSuccess: boolean = true;
    public isVisible: boolean = true;

    private deck?: DeckBaseComponent;
    private deckAlternateCardModal!: DeckAlternateCardModalComponent;
    private switchCardVersionsSub!: Subscription;
    private addAlternateCardVersionSub!: Subscription;

    public constructor(
        private ponderDataClient: PonderDataClient, 
        private pageTitle: Title,
        private events: EventBroker) {

        this.switchCardVersionsSub = this.events.deck.displaySwitchCardVersionEvent.subscribe({
            next: (deckCard: DeckCard | undefined) => {
                this.deckAlternateCardModal.reset(deckCard, AlternateCardModalAction.SwitchVersion);
            }
        });

        this.addAlternateCardVersionSub = this.events.deck.displayAddAlternateCardVersionEvent.subscribe({
            next: (deckCard: DeckCard | undefined) => {
                this.deckAlternateCardModal.reset(deckCard, AlternateCardModalAction.AddVersion);
            }
        });
    }

    public ngOnDestroy(): void {
        this.switchCardVersionsSub?.unsubscribe();
        this.addAlternateCardVersionSub?.unsubscribe();
    }

    public ngAfterViewInit(): void {
        this.startDeckGet();
    }

    private startDeckGet(): void {
        this.ponderDataClient
            .deck(this.deckId)
            .pipe(take(1))
            .subscribe({
                next: (response: GetDeckResponse) => {
                    if (response.success) {
                        this.loadingSuccess = true;

                        if (response.isVisible) {
                            this.onGetDeckSuccess(response);
                        } else {
                            this.isVisible = false;
                        }
                    }
                }
            })
            .add(() => { this.isLoading = false; });
    }

    private onGetDeckSuccess(response: GetDeckResponse): void {
        this.updateTitleAndNavigation(response);

        response.deck?.layers?.forEach(l => {
            l.cards?.sort((a, b) => {
                if (a.card.item.name! === b.card.item.name!) {
                    return 0;
                } else {
                    return (a.card.item.name! < b.card.item.name! ? -1 : 1);
                }
            });
        });

        switch (response.deck!.game) {
            case GameType.Magic:
                this.deck = this.deckVcr.createComponent(MagicDeckComponent).instance;
                this.deck.initialize(response.deck!, response.deck!.layers![0]);
                this.deck.cdr.detectChanges();
                break;

            default:
                break;
        }

        this.createAlternateCardModal();
    }

    private updateTitleAndNavigation(response: GetDeckResponse): void {
        this.pageTitle.setTitle(`Ponder > ${this.username} > ${response.deck!.name}`);

        var navSegments = initializeUserSegmentArray(this.username);
        addSegmentToList(navSegments, "Decks", "Decks", `/user/${this.username}/decks`);
        addSegmentToList(navSegments, response.deck!.name!, response.deck!.name!);
        
        this.events.ui.navbarUpdateEvent.emit(navSegments);
    }

    private createAlternateCardModal(): void {
        if (this.deck) {
            this.deckAlternateCardModal = this.deckVcr.createComponent(DeckAlternateCardModalComponent).instance;
            this.deckAlternateCardModal.deckComponent = this.deck;
        }
    }
}